import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Loader2, Plus, Trash2, Save, Users, Lock } from "lucide-react";
import { vaultGet, vaultSave } from "../lib/api";
import { toast } from "sonner";

const FIELDS = [
  { key: "fullName", label: "Full name", ph: "Sohel Patel" },
  { key: "address", label: "Address", ph: "123 Main St, Chicago, IL" },
  { key: "phone", label: "Phone", ph: "+1 312 555 0100" },
  { key: "email", label: "Email", ph: "you@email.com" },
  { key: "dob", label: "Date of birth", ph: "1980-01-01" },
  { key: "birthPlace", label: "Place of birth", ph: "Mumbai, India" },
  { key: "nationality", label: "Nationality", ph: "USA" },
  { key: "idNumber", label: "Passport / ID number", ph: "P1234567" },
];

export default function MasterVault({ userId }) {
  const [p, setP] = useState({ family: [], custom: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await vaultGet(userId); const pr = r.profile || {}; setP({ family: [], custom: [], ...pr }); }
    catch (e) { toast.error("Could not load your vault"); }
    finally { setLoading(false); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const setFam = (i, k, v) => setP((x) => { const f = [...(x.family || [])]; f[i] = { ...f[i], [k]: v }; return { ...x, family: f }; });
  const addFam = () => setP((x) => ({ ...x, family: [...(x.family || []), { name: "", relation: "", dob: "" }] }));
  const delFam = (i) => setP((x) => ({ ...x, family: (x.family || []).filter((_, j) => j !== i) }));
  const setCus = (i, k, v) => setP((x) => { const c = [...(x.custom || [])]; c[i] = { ...c[i], [k]: v }; return { ...x, custom: c }; });
  const addCus = () => setP((x) => ({ ...x, custom: [...(x.custom || []), { label: "", value: "" }] }));
  const delCus = (i) => setP((x) => ({ ...x, custom: (x.custom || []).filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try { await vaultSave(userId, p); toast.success("Saved — Nexus will use this automatically"); }
    catch (e) { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const inp = "w-full bg-[#0a1526] text-cyan-50 border border-[#00F0FF33] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]";

  if (loading) return <div className="lg:col-span-2 nx-glass rounded-2xl p-10 flex justify-center"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>;

  return (
    <div className="lg:col-span-2 nx-glass rounded-2xl p-4 space-y-4" data-testid="vault-panel">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "radial-gradient(circle at 40% 30%, #00F0FF, #061018 75%)", boxShadow: "0 0 20px #00F0FF66" }}><ShieldCheck className="w-5 h-5 text-white" /></div>
        <div>
          <h2 className="nx-orbitron font-black tracking-[0.2em] text-[#00F0FF] text-base leading-none">MASTER VAULT</h2>
          <p className="text-cyan-100/45 text-[10px] nx-mono mt-0.5 flex items-center gap-1"><Lock className="w-3 h-3" /> Encrypted · auto-fills every letter &amp; doc</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-cyan-200/50 text-[11px] nx-mono">{f.label.toUpperCase()}</label>
            <input className={inp} value={p[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.ph} data-testid={`vault-${f.key}`} />
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#00F0FF]" /><p className="text-cyan-200/60 text-[11px] nx-mono tracking-widest">FAMILY</p>
          <button onClick={addFam} data-testid="vault-add-family" className="ml-auto px-2.5 py-1 rounded-lg nx-mono text-[11px] text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
        </div>
        {(p.family || []).map((m, i) => (
          <div key={i} className="flex gap-2" data-testid="vault-family-row">
            <input className={inp} value={m.name || ""} onChange={(e) => setFam(i, "name", e.target.value)} placeholder="Name" data-testid="vault-family-name" />
            <input className={inp} value={m.relation || ""} onChange={(e) => setFam(i, "relation", e.target.value)} placeholder="Relation" />
            <input className={`${inp} sm:w-40`} value={m.dob || ""} onChange={(e) => setFam(i, "dob", e.target.value)} placeholder="DOB" />
            <button onClick={() => delFam(i)} data-testid="vault-family-del" className="p-2 rounded-lg text-cyan-100/40 hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2"><Plus className="w-4 h-4 text-[#00F0FF]" /><p className="text-cyan-200/60 text-[11px] nx-mono tracking-widest">ANYTHING ELSE (custom fields)</p>
          <button onClick={addCus} data-testid="vault-add-custom" className="ml-auto px-2.5 py-1 rounded-lg nx-mono text-[11px] text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
        </div>
        {(p.custom || []).map((c, i) => (
          <div key={i} className="flex gap-2" data-testid="vault-custom-row">
            <input className={`${inp} sm:w-52`} value={c.label || ""} onChange={(e) => setCus(i, "label", e.target.value)} placeholder="Label (e.g. Business)" />
            <input className={inp} value={c.value || ""} onChange={(e) => setCus(i, "value", e.target.value)} placeholder="Value" />
            <button onClick={() => delCus(i)} data-testid="vault-custom-del" className="p-2 rounded-lg text-cyan-100/40 hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <button onClick={save} disabled={saving} data-testid="vault-save" className="w-full py-3 rounded-xl nx-orbitron font-bold text-[#030712] bg-[#00F0FF] disabled:opacity-40 flex items-center justify-center gap-2">{saving ? <><Loader2 className="w-5 h-5 animate-spin" /> Saving…</> : <><Save className="w-5 h-5" /> Save to Vault</>}</button>
      <p className="text-cyan-100/35 text-[10px] nx-mono text-center">Stored with AES-256 encryption. Only your Master access can read it — and Nexus uses it to fill your documents.</p>
    </div>
  );
}
